import { i18n } from "@pathwright/ui/src/components/lng/I18nextProvider"
import flow from "lodash/flow"
import deprecatedCommonTranslations from "./translations/en/deprecatedCommon.json"

const capitalize = (str) =>
  str ? `${str[0].toUpperCase()}${str.slice(1)}` : ""

const capitalizeAllWords = (str) =>
  str ? str.split(" ").map(capitalize).join(" ") : str

// format a translation based on the format option provided to the
// translate function
export const addTranslationFormatters = (i18next) => {
  i18next.services.formatter.add("lowercase", (value, lng, options) => {
    return value.toLowerCase()
  })

  i18next.services.formatter.add("uppercase", (value, lng, options) => {
    return value.toUpperCase()
  })

  i18next.services.formatter.add("capitalize", (value, lng, options) => {
    return capitalize(value)
  })
}

// These keys correspod directly to keys used in the translation json files. Update with care!
export const getTranslateMethods = (translator) => ({
  t: translator,
  tc: flow([translator, capitalize]),
  tca: flow([translator, capitalizeAllWords]),
  conditionalT: (key, options, customTranslator) =>
    i18n.exists(key) ? (customTranslator || translator)(key, options) : ""
})

/**
 * translateWrapper enables interpolating translation keys within translation values, i.e.:
 *
 * "cohort": {
 *   "label": "cohort",
 *   "new": "New t('cohort.label')"
 * }
 */

// DEPRECATED: using a preprocessor to transform these custom translate methods to proper i18next nested keys.
// export const customTranslate = translator => {
//   const translateMethods = getTranslateMethods(translator)
//   const translateKeys = Object.keys(translateMethods)
//   // group the (translator key) and (translation key)
//   const re = new RegExp(
//     `(${translateKeys.join("|")})\\('([a-zA-Z0-9._-]+)'\\)`,
//     "g"
//   )

//   return (translationStr, ...rest) => {
//     try {
//       return translator(translationStr, ...rest).replace(
//         re,
//         (matchedSubstr, translatorKey, translationKey) =>
//           translateMethods[translatorKey](translationKey)
//       )
//     } catch (error) {
//       console.error(`Failed to translate "${translationStr}": ${error}`)
//     }
//   }
// }

// Handles processing legacy custom translator methods to proper i18next nested keys.
export const preProcessTranslation = (() => {
  // group the (translator key) and (translation key)
  const re = new RegExp(
    `(?<translationMethod>t|tc|tca)\\('(?<translationKey>[a-zA-Z0-9._-]+)'\\)`,
    "g"
  )

  const process = (...args) => {
    // Get grouped match
    const { translationMethod, translationKey } = args.slice(-1)[0]
    switch (translationMethod) {
      case "t":
        return `$t(${translationKey})`
      case "tc":
        return `$t(${translationKey}, capitalize)`
      case "tca":
        return `$t(${translationKey}, capitalize_all)`
    }
  }

  const fn = (translationJSON) => {
    if (!translationJSON) return {}

    return Object.entries(translationJSON).reduce((json, [key, value]) => {
      // if value is a string, record the path/value
      if (typeof value === "string") {
        return {
          ...json,
          [key]: value.replace(re, process)
        }
      }

      // otherwise, continue to flatten the json
      return {
        ...json,
        [key]: fn(value)
      }
    }, {})
  }

  return fn
})()

// For local testing
export const getTranslation = async (language, namespace) => {
  if (!language) {
    return null
  }

  const translation = await import(
    `@pathwright/ui/src/components/lng/translations/${language}/${namespace}.json`
    /* webpackChunkName: "translation-json" */
  )

  return {
    translation,
    language,
    id: "foo"
  }
}

// NOTE: this function mutates the translation object!
// Apply deprecated translations to the provided translation. This maps
// deprecated translation keys to new translation keys. This is useful for
// maintaining backwards compatibility with older translations while
// transitioning to new keys.

// The hope is to handle this rather at the db level where deploying an updated
// translation will automatically update the translation keys in the db for all
// affected translations.

// One gap with this method currently is that the translation variables are not updated
// in the legacy translations, so the client must still pass in the legacy translation
// variables to the translation function. We'll want to fix this in the future.
// Another gab: we should have the client attempt to generate the deprecated translation
// json when new keys are added to the translation json to further automate this process.

// The deprecated common json translation is a list of maps of flattened deprecated translation paths
// to a new translation path. This fn applies the map to the provided translation.
// Add a new translation map per commit, adding to the end of the array so we apply
// the most recent translations last.
export function applyDeprecatedTranslations(translation) {
  // Now, map any deprecated keys to the new keys.
  deprecatedCommonTranslations.forEach((deprecatedCommonTranslation) => {
    Object.entries(deprecatedCommonTranslation).forEach(
      ([deprecatedPath, newPath]) => {
        // NOTE: this keySeparatorRe is ported over from the feat/home-screen branch,
        // but also is slightly improved to handle preceding/following spaces.
        const keySeparatorRe = /(?<=[^\. ])\.(?=[^\. ])(?!(?:_one|_other)$)/
        let deprecatedKeys = deprecatedPath.split(keySeparatorRe)
        let newKeys = newPath.split(keySeparatorRe)
        let deprecatedTranslation = translation
        let newTranslation = translation

        // Loop through the deprecated keys to find if the deprecated translation path exists.
        while (deprecatedKeys.length) {
          const deprecatedKey = deprecatedKeys.shift()
          if (deprecatedTranslation[deprecatedKey]) {
            // When no more keys exist, we can try mapping the deprecated translation to the
            // new translation path.
            if (!deprecatedKeys.length) {
              // Loop through the new keys to find if the new translation path exists.
              while (newKeys.length) {
                const newKey = newKeys.shift()
                if (newTranslation[newKey]) {
                  // When no more keys exist, we can try mapping the deprecated translation to the
                  // new translation path.
                  if (!newKeys.length) {
                    // Finally mapping the deprecated translation value to the new translation path.
                    newTranslation[newKey] =
                      deprecatedTranslation[deprecatedKey]
                  }
                } else {
                  // Break out when we reach a key that doesn't exist in the new translation path.
                  break
                }

                // Update the new translation to the nested object.
                newTranslation = newTranslation[newKey]
              }
            }
          } else {
            // Break out when we reach a key that doesn't exist in the deprecated translation path.
            break
          }

          // Update the deprecated translation to the nested object.
          deprecatedTranslation = deprecatedTranslation[deprecatedKey]
        }
      }
    )
  })

  return translation
}
